import { AlertCircle, QrCode, Save } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import {
  PayinAmountWithFee,
  type PayinAmountWithFeeState,
} from "@/shared/components/payments/PayinAmountWithFee";
import { quickAmountsFor } from "@/shared/components/payments/payinQuickAmounts";
import useTranslations from "@/shared/lang/useTranslations";

import { usePayinAmountLabels, usePayinRailHint } from "../../hooks/usePayinAmountLabels";
import { isValidPayerDocument, PayinRail } from "../../services/paymentsPayin.service";

export interface PayinBrazilFormValues {
  payerName: string;
  /** CPF/CNPJ as typed; the payload builder strips it to digits. */
  payerDocument: string;
  /** Gross BRL amount the payer transfers, as the preview resolved it (decimal string). */
  grossAmount: string;
}

export interface PayinBrazilFormProps {
  /** Org the payin is created for — also scopes the fee preview to its effective price book. */
  organizationId: string | null;
  /** Receives validated values; the host page builds the runtime payload and performs the request. */
  onSubmit: (values: PayinBrazilFormValues) => void | Promise<void>;
  isSubmitting: boolean;
}

const buildSchema = (nameRequired: string, nameMax: string, documentInvalid: string) =>
  z.object({
    payerName: z.string().min(1, nameRequired).max(100, nameMax),
    payerDocument: z.string().min(1, documentInvalid).refine(isValidPayerDocument, documentInvalid),
  });

type FormValues = z.infer<ReturnType<typeof buildSchema>>;

const DEFAULTS: FormValues = { payerName: "", payerDocument: "" };

/**
 * Brazil creation form for the payments runtime (`POST /internal/payments/payins`, rail PIX). Unlike
 * Colombia, the payer identity (name + CPF/CNPJ) is REQUIRED — the provider needs it to mint the dynamic Pix
 * QR — so those two fields stay under React Hook Form while the amount lives in the shared amount+fee
 * control: it is not a plain number but the output of a live pricing call, and validating it with Zod would
 * duplicate the guards the preview endpoint already applies.
 *
 * The submit sends the preview's resolved gross, so the charge always matches the numbers the user saw.
 */
export function PayinBrazilForm({ organizationId, onSubmit, isSubmitting }: PayinBrazilFormProps) {
  const { t } = useTranslations("payins");
  const labels = usePayinAmountLabels();
  const railHint = usePayinRailHint(PayinRail.PIX);
  const [priced, setPriced] = useState<PayinAmountWithFeeState>({
    mode: "gross",
    preview: null,
    amount: null,
    loading: false,
    error: null,
  });

  const schema = useMemo(
    () =>
      buildSchema(
        t("validation_payer_name_required"),
        t("validation_payer_name_max"),
        t("validation_payer_document"),
      ),
    [t],
  );
  const form = useZodForm(schema, DEFAULTS);

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (!priced.preview) return;
      void onSubmit({
        payerName: values.payerName.trim(),
        payerDocument: values.payerDocument.trim(),
        grossAmount: priced.preview.gross_amount,
      });
    },
    [priced.preview, onSubmit],
  );

  return (
    <ColocaForm form={form} onSubmit={handleSubmit}>
      <ColocaAlert variant="secondary" icon={QrCode} text={railHint} />

      {!organizationId && <ColocaAlert variant="destructive" icon={AlertCircle} text={t("error_no_org")} />}

      <ColocaInputField
        name="payerName"
        label={t("form_payer_name_label")}
        placeholder={t("form_payer_name_placeholder")}
      />
      <ColocaInputField
        name="payerDocument"
        label={t("form_payer_document_label")}
        placeholder={t("form_payer_document_placeholder")}
        inputMode="numeric"
      />

      <PayinAmountWithFee
        organizationId={organizationId}
        rail={PayinRail.PIX}
        country="BRA"
        currency="BRL"
        labels={labels}
        quickAmounts={quickAmountsFor("BRL")}
        onChange={setPriced}
      />

      <ColocaButton
        type="submit"
        variant="primary"
        icon={Save}
        loading={isSubmitting}
        disabled={!priced.preview || isSubmitting}
        className="w-full"
      >
        {priced.mode === "gross" ? t("submit_gross") : t("submit_net")}
      </ColocaButton>
    </ColocaForm>
  );
}
